<template>
  <div class="visit-packages-and-materials-new">
    <div class="row align-items-start">
      <div :class="{ 'col-md-6': !editDisabled, 'col-md-12 mb-3': editDisabled }">
        <div class="font-weight-bold"
             :class="{ 'mb-2': !editDisabled }">
          {{ $t('kits_and_materials') }}
        </div>
        <ServiceMultiSelect :dblclick="true"
                            v-if="!editDisabled"
                            :placeholder="$t('add_kit')"
                            class="mb-0"
                            model="kit"
                            :key="`updated-${updatedCounter}`"
                            :readonly="editDisabled"
                            @select="packageAdded"/>
        <div v-if="packages.length"
             class="d-flex flex-wrap align-items-center gap-2 mt-3">
          <div v-for="(pack, pInd) in packages"
               class="package-teaser-card"
               :key="`pack-${pInd}`">
            <div>
              {{ pack.name || pack.title }}
            </div>
            <div v-if="!editDisabled" @click="removePackage(pack)">
              <CloseSvg class="svg-black svg-size-xs"/>
            </div>
          </div>
        </div>
        <!--      <div v-if="packages && packages.length > 0"-->
        <!--           class="text-muted small">-->
        <!--        {{ $t('packages_cost') }}: {{ formatCurrency(packagesCost) }}-->
        <!--      </div>-->
      </div>
      <div :class="{ 'col-md-6': !editDisabled, 'col-md-12': editDisabled }">
        <Autocomplete :placeholder="$t('add_material')+'...'"
                      v-if="!editDisabled"
                      model="material"
                      class="template-like mb-0"
                      @input="materialAdded"/>
        <div :class="{ 'mt-4': !editDisabled }"
             v-if="materials.length > 0">
          <div>
            <div class="patient-visit-services-table w-100">
              <div class="table-header font-weight-bold row no-gutters">
                <div class="col-7 col-td">{{ $t('material') }}</div>
                <div class="col-td col-2">{{ $t('amount') }}</div>
                <div class="col-td col-2">{{ $t('unit_of_measure') }}</div>
                <div class="col-td col-1"></div>
              </div>
<!--              <div v-for="(m, mind) in materials"-->
<!--                  class="position-relative table-row row no-gutters"-->
<!--                  :key="`material-${m.material_id}-${mind}`">-->
<!--                <div :data-title="$t('material')" class="order-mob-0 col-7 col-td">-->
<!--                  {{ m.name }}-->
<!--                </div>-->
<!--                <div :data-title="$t('amount')" class="col-td col-2">-->
<!--                  <QuantityClicker :readonly="editDisabled"-->
<!--                                   @minus="minusMaterial(mind)"-->
<!--                                   @plus="plusMaterial(mind)"-->
<!--                                   @amount="(...args) => materialAmountChanged(mind, ...args)"-->
<!--                                   :amount="m.pivot.amount"/>-->
<!--                </div>-->
<!--                <div :data-title="$t('unit_of_measure')" class="col-td col-2">-->
<!--                  {{ units[m.unit_id] }}-->
<!--                </div>-->
<!--                <div class="diagnosis-delete-td text-right col-td col-1">-->
<!--                  <a class="pointer"-->
<!--                     v-if="!editDisabled"-->
<!--                     @click="deleteMaterial(m)">-->
<!--                    <TrashSvg class="svg-stroke-red"/>-->
<!--                  </a>-->
<!--                </div>-->
<!--              </div>-->
              <div v-for="(m, mind) in materialsGrouped"
                   class="position-relative table-row row no-gutters align-items-center"
                   :key="`material-${m.material_id}-${mind}`">
                <div :data-title="$t('material')"
                     class="order-mob-0 col-7 col-td">
                  {{ m.name }}
                </div>
                <div :data-title="$t('amount')" class="col-td col-2">
                  <QuantityClicker :readonly="editDisabled"
                                   @minus="minusMaterialFromGroup(m.material_id)"
                                   @plus="plusMaterialFromGroup(m.material_id)"
                                   @amount="(...args) => materialAmountChangedFromGroup(m.material_id, ...args)"
                                   :amount="Number(m.pivot.amount)"/>
                </div>
                <div :data-title="$t('unit_of_measure')" class="col-td col-2">
                  {{ units[m.unit_id] }}
                </div>
                <div class="diagnosis-delete-td text-right col-td col-1">
                  <a class="table-btn delete-trash-btn"
                     v-if="!editDisabled"
                     @click="deleteMaterial(m)">
                    <TrashSvg class="svg-stroke-red"/>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import ServiceMultiSelect from "@/components/form/ServiceMultiSelect.vue"
import TrashSvg from "@/assets/svg-vue/general/delete2.svg"
import CloseSvg from '@/assets/svg-vue/general/close.svg'
import Autocomplete from "@/components/parts/general/Autocomplete.vue"
import {computed, ref} from "vue"
import {useStore} from "vuex"
import QuantityClicker from "@/components/parts/general/QuantityClicker.vue"
import {roundToTwoDecimals} from "@/services/number.service"

const props = defineProps({
  service: Object,
  readonly: Boolean,
})

const store = useStore()

const rights = computed(() => store.state.auth.rights || [])
const units = computed(() => store.state.auth.units || [])
const hasRightToEditMaterials = computed(() => rights.value.includes('treatment_materials'))
const editDisabled = computed(() => props.readonly || !hasRightToEditMaterials.value)
const materials = computed(() => {
  return store.getters.recordMaterialsByServiceIds[props.service.id] || []
})
const materialsGrouped = computed(() => {
  const map = new Map()
  for (const m of materials.value) {
    if (map.has(m.material_id)) {
      map.get(m.material_id).pivot.amount += Number(m.pivot.amount)
    } else {
      map.set(m.material_id, { ...m, pivot: { ...m.pivot, amount: Number(m.pivot.amount) } })
    }
  }
  return Array.from(map.values())
})
const packages = computed(() => {
  return store.getters.recordPackagesByServiceIds[props.service.id] || []
})
const updatedCounter = ref(0)

const materialAdded = (material) => {
  store.dispatch('addMaterialToRandomToothWithService', {
    serviceId: props.service.id,
    material
  })
}

const packageAdded = (pack) => {
  store.dispatch('addPackageToSummaryService', {
    serviceId: props.service.id,
    pack
  })
  updatedCounter.value++
}

const deleteMaterial = (material) => {
  store.dispatch('removeMaterialFromEachService', {
    serviceId: props.service.id,
    material
  })
}

const removePackage = (pack) => {
  store.dispatch('removePackageFromSummaryService', {
    serviceId: props.service.id,
    pack
  })
}

// const plusMaterial = (index) => {
//   materials.value[index].pivot.amount = roundToTwoDecimals(materials.value[index].pivot.amount + 1)
// }
//
// const minusMaterial = (index) => {
//   materials.value[index].pivot.amount = roundToTwoDecimals(materials.value[index].pivot.amount - 1)
//   if(materials.value[index].pivot.amount <= 0) {
//     deleteMaterial(materials.value[index])
//   }
// }
//
// const materialAmountChanged = (index, val) => {
//   materials.value[index].pivot.amount = roundToTwoDecimals(val)
// }

const plusMaterialFromGroup = (materialId) => {
  const item = findMaterialFromId(materialId)
  if(item) item.pivot.amount = roundToTwoDecimals(item.pivot.amount + 1)
}

const minusMaterialFromGroup = (materialId) => {
  const item = findMaterialFromId(materialId, 'maximumAmount')
  if(item) {
    item.pivot.amount = roundToTwoDecimals(item.pivot.amount - 1)
    if(item.pivot.amount <= 0) {
      deleteMaterial(item)
    }
  }

}

const materialAmountChangedFromGroup = (materialId, val) => {
  const item = findMaterialFromId(materialId)
  item.pivot.amount = roundToTwoDecimals(val)
}

const findMaterialFromId = (materialId, type = 'minimumAmount') => {
  const candidates = materials.value.filter(m => m.material_id === materialId)
  if (!candidates.length) return null
  if(type === 'minimumAmount') {
    return candidates.reduce((min, m) => m.pivot.amount < min.pivot.amount ? m : min, candidates[0])
  }
  if(type === 'maximumAmount') {
    return candidates.reduce((min, m) => m.pivot.amount > min.pivot.amount ? m : min, candidates[0])
  }
  return candidates[0]
}
</script>

<style scoped lang="scss">
.visit-packages-and-materials-new {
  border-top: 1px dashed $border-new-color;
  padding: 16px;
}
.package-teaser-card {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  display: flex;
  align-items: center;
  min-height: 24px;
  padding: 4px 12px;
  gap: 8px;
}
</style>
